// Story 11.1 — Árbol de archivos lateral del editor.

import {
  ChevronDown,
  ChevronRight,
  Clock,
  FileCode2,
  FileType,
  GitBranch,
  Lock,
  TestTube,
  Volume2,
} from 'lucide-react';
import { useState } from 'react';
import type { FileKind, FileTreeFile, FileTreeNode } from '@/lib/editor-types';
import { cn } from '@/lib/utils';

interface Props {
  tree: FileTreeNode[] | undefined;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  isLoading?: boolean;
}

const KIND_ICON: Record<FileKind, React.ComponentType<{ className?: string }>> = {
  'bot-config': FileCode2,
  flow: GitBranch,
  voice: Volume2,
  hours: Clock,
  test: TestTube,
  'ext-ts': FileType,
  unknown: FileCode2,
};

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6'] as const;

export function FileTree({
  tree,
  selectedPath,
  onSelect,
  isLoading = false,
}: Props): React.ReactElement {
  if (isLoading || !tree) {
    return (
      <div className="space-y-1 p-2">
        {SKELETON_KEYS.map((k) => (
          <div key={k} className="h-6 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }
  return (
    <ul className="space-y-0.5 p-2 text-sm" aria-label="Árbol de archivos">
      {tree.map((node) => (
        <Node
          key={node.path}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

interface NodeProps {
  node: FileTreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}

function Node({ node, depth, selectedPath, onSelect }: NodeProps): React.ReactElement {
  const [expanded, setExpanded] = useState(true);
  if (node.type === 'dir') {
    return (
      <li>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-muted-foreground',
            'hover:bg-accent hover:text-accent-foreground',
          )}
          style={{ paddingLeft: depth * 12 + 4 }}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden />
          )}
          <span className="font-medium">{node.name}</span>
        </button>
        {expanded && (
          <ul className="mt-0.5 space-y-0.5">
            {node.children.map((child) => (
              <Node
                key={child.path}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }
  return (
    <FileItem node={node} depth={depth} selected={selectedPath === node.path} onSelect={onSelect} />
  );
}

interface FileItemProps {
  node: FileTreeFile;
  depth: number;
  selected: boolean;
  onSelect: (path: string) => void;
}

function FileItem({ node, depth, selected, onSelect }: FileItemProps): React.ReactElement {
  const Icon = KIND_ICON[node.kind] ?? FileCode2;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(node.path)}
        className={cn(
          'flex w-full items-center gap-2 rounded px-1 py-0.5 text-left transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          selected && 'bg-accent text-accent-foreground',
        )}
        style={{ paddingLeft: depth * 12 + 4 }}
        aria-current={selected ? 'page' : undefined}
        data-path={node.path}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{node.name}</span>
        {node.readOnly && (
          <Lock
            className="ml-auto h-3 w-3 shrink-0 text-muted-foreground"
            aria-label="Solo lectura"
          />
        )}
      </button>
    </li>
  );
}
